<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import { Head, Link, router } from '@inertiajs/vue3';
import Swal from 'sweetalert2';
import { trans as $t } from 'laravel-vue-i18n';
</script>

<script>
const bootstrap = typeof window !== `undefined` && import('bootstrap');

export default {
    props: {
        errors: {
            type: Object
        },
    },
    data() {
        return {
            ajaxRequest: false,
            username: '',
            password: '',
            remember: true
        }
    },
    methods: {

        loginUser() {
            router.post(route('authenticate'), {
                username: this.username,
                password: this.password,
                remember: this.remember,
                redirect: true
            }, {
                onStart: () => {
                    this.ajaxRequest = true;
                },
                onSuccess: page => {
                    if (dataLayer) {
                        dataLayer.push({
                            event: 'login',
                        });
                    }
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });
        },

        // Show password reminder modal
        showReminderModal() {

            bootstrap?.then(({ Modal }) => {
                Modal.getOrCreateInstance('#reminderModal').show();
            });

        },

        // Send password reminder e-mail
        sendReminderMail() {

            this.ajaxRequest = true;
            router.post(route('doPasswordReminder'), {
                reminderusername: this.$refs.reminderUsername.value
            }, {
                only: ['errors'],
                // Do not scroll this page if we have errors
                // preserveScroll: (page) => Object.keys(page.props.errors).length,
                preserveScroll: true,
                onSuccess: page => {

                    bootstrap?.then(({ Modal }) => {
                        Modal.getOrCreateInstance('#reminderModal').hide();
                    });

                    Swal.fire({
                        icon: 'success',
                        text: $t('A jelszó visszaállításához szükséges e-mailt elküldtük Önnek!'),
                    });
                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    }
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });

        },

    },
    mounted() {

        // Page view DataLayer event
        if (dataLayer) {
            dataLayer.push({
                event: 'virtualPageView',
                page_type: 'other',
                site_language: this.$page.props.locale?.toUpperCase() || '?',
                login_status: this.$page.props.user ? 'logged_in' : 'logged_out',
                user_id: this.$page.props.user ? this.$page.props.user.id : null,
            });
        }

    }
}
</script>

<template>

    <Head :title="$t('Bejelentkezés')" />

    <AppLayout>

        <header class="header">
            <div class="header-body header-body-subpage">
                <div class="container">
                    <div class="row align-items-center justify-content-center">
                        <div class="col-12 col-sm-auto col-lg text-center text-sm-right text-lg-left">
                            <h1 class="header-h1">{{ $t('Bejelentkezés') }}</h1>
                        </div>
                    </div>
                </div>
            </div>
        </header>

        <div class="header-placeholder"></div>

        <div class="subpage-content">
            <div class="container subpage-container">
                <div class="row">
                    <main class="main col-12">
                        <section>
                            <div class="row section-title d-none">
                                <div class="col-12">
                                    <h2>{{ $t('Bejelentkezés') }}</h2>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-6 mb-3">
                                    <form @submit.prevent="loginUser">
                                        <p class="fs-5 text-center">{{ $t('Ha már van fiókja, kérjük, felhasználónevével és jelszavával jelentkezzen be!') }}</p>
                                        <div v-if="errors.form" class="alert alert-danger fw-bold mb-3" role="alert">{{ errors.form }}</div>
                                        <div class="form-floating mb-3">
                                            <input type="text" class="form-control" :class="{ 'is-invalid': errors.username }" id="username" name="username" :placeholder="$t('Felhasználói név') + ' / ' + $t('E-mail cím')" v-model="username" autocomplete="username">
                                            <label for="username">{{ $t('Felhasználói név') + ' / ' + $t('E-mail cím') }}<span class="text-danger">*</span></label>
                                            <div v-if="errors.username" class="invalid-feedback">{{ errors.username }}</div>
                                        </div>
                                        <div class="form-floating mb-3">
                                            <input type="password" class="form-control" :class="{ 'is-invalid': errors.password }" id="password" name="password" :placeholder="$t('Jelszó')" v-model="password" autocomplete="current-password">
                                            <label for="password">{{ $t('Jelszó') }}<span class="text-danger">*</span></label>
                                            <div v-if="errors.password" class="invalid-feedback">{{ errors.password }}</div>
                                        </div>
                                        <div class="mb-5 text-center">
                                            <button type="submit" class="btn btn-primary px-5 text-uppercase">{{ $t('Bejelentkezés') }}</button>
                                        </div>
                                        <div class="text-center">
                                            <a href="javascript:;" class="text-secondary" @click="showReminderModal">{{ $t('Elfelejtettem a jelszavamat') }}</a>
                                        </div>
                                    </form>
                                </div>
                                <div class="col-md-6 mb-3 text-center">
                                    <div class="mb-4">
                                        <p class="fs-6">{{ $t('Ha csatlakozni szeretne a miniBUD hálózatához, kérjük töltse ki a regisztrációs űrlapot!') }}</p>
                                        <Link :href="route('registration')" class="btn btn-primary px-5 text-uppercase">{{ $t('Regisztráció') }}</Link>
                                    </div>
                                    <!--
                                    <div class="mb-4">
                                        <p class="fs-6"
                                            v-html="$t('Ha szeretne csatlakozni a miniBUD üzleti partneri hálózatához, kérjük, töltse ki a regisztrációs űrlapot. Ezt követően a miniBUD repülőtéri transzfert és a további szolgáltatásokat kedvezménnyel foglalhatja majd.')">
                                        </p>
                                        <Link :href="route('b2bRegistration')" class="btn btn-secondary px-5 text-uppercase">{{ $t('B2B Regisztráció') }}</Link>
                                    </div>
                                    -->
                                </div>
                            </div>
                        </section>
                    </main>
                </div>
            </div>
        </div>

        <div id="reminderModal" class="modal fade" tabindex="-1">
            <div class="modal-dialog modal-fullscreen-md-down">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ $t('Elfelejtett jelszó') }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-12">
                                <p>{{ $t('Elfelejtette jelszavát? Adja meg a regisztrált felhasználói nevét vagy e-mail címét, és segítünk Önnek!') }}</p>
                            </div>
                            <div class="col-12">
                                <div class="form-floating mb-3">
                                    <input type="text" class="form-control" :class="{ 'is-invalid': errors.reminderusername }" id="reminderUsername" :placeholder="$t('Felhasználói név')" ref="reminderUsername">
                                    <label for="reminderUsername">{{ $t('Felhasználói név') }}<span class="text-danger">*</span></label>
                                    <div v-if="errors.reminderusername" class="invalid-feedback">{{ errors.reminderusername }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('Mégsem') }}</button>
                        <button type="button" class="btn btn-primary" :disabled="ajaxRequest" @click="sendReminderMail">{{ $t('Küldés') }}</button>
                    </div>
                </div>
            </div>
        </div>

    </AppLayout>
</template>
